/**
 * feat-305 — the page-facing entry point (ADR 0001 §10.6; NOTES §2 "Entry control").
 *
 *   <AssistantLauncher tenantId pageContext copy onOpenChange />
 *
 * Renders NOTHING unless the tenant's config says `enabled` and lists this
 * page — and nothing while config loads or when it fails, so a page without
 * the assistant (or with a broken config call) is byte-for-byte unaffected
 * (spec behavior 10). It also renders nothing when the page can't build a
 * context (`pageContext === null`).
 *
 * Owns: the band button, the floating re-entry pill, open/close, focus return,
 * the docked/sheet presentation switch (matchMedia ≥ 1024px), the nav-height
 * measurement, and the conversation hook (so state survives close/reopen).
 * Closing while an answer streams stops it (DESIGN-QUESTION Q3).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Sparkles } from 'lucide-react';
import { getAssistantConfig } from '../../services/assistantService';
import type { PageContext } from './types';
import type { AssistantPageCopy } from './pages';
import { AssistantPanel, AssistantPanelBoundary, ASSISTANT_PANEL_ID } from './AssistantPanel';
import { useAssistantConversation } from './useAssistantConversation';
import { ASSISTANT_COPY } from './copy';

const LG_QUERY = '(min-width: 1024px)';
/** The app's sticky nav (ScorecardNavigation root). Measured, never hard-coded. */
const NAV_SELECTOR = '[data-app-nav]';

function useIsDesktop(): boolean {
  const get = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(LG_QUERY).matches
      : true;
  const [desktop, setDesktop] = useState(get);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(LG_QUERY);
    const on = () => setDesktop(mq.matches);
    on();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', on);
      return () => mq.removeEventListener('change', on);
    }
    mq.addListener(on);
    return () => mq.removeListener(on);
  }, []);
  return desktop;
}

function useNavHeight(active: boolean): number {
  const [h, setH] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const nav = document.querySelector<HTMLElement>(NAV_SELECTOR);
    if (!nav) return undefined;
    const measure = () => setH(nav.getBoundingClientRect().height);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(nav);
    return () => ro.disconnect();
  }, [active]);
  return h;
}

export interface AssistantLauncherProps {
  tenantId: string | null | undefined;
  pageContext: PageContext | null;
  copy: AssistantPageCopy;
  /** The page reflows beside the docked panel (`lg:pr-[380px] xl:pr-[420px]`) while this is true. */
  onOpenChange?: (open: boolean) => void;
  /**
   * Optional slot for the band button. When given, the launcher can be mounted
   * at a STABLE place in the page (so a page-level loading state that swaps
   * out the header doesn't unmount the conversation or close the panel) while
   * the button renders into this element. `null` = the slot isn't on screen
   * right now (no button, no pill). Omitted = the button renders inline.
   */
  entryContainer?: HTMLElement | null;
}

type ConfigState = 'loading' | 'on' | 'off';

export const AssistantLauncher: React.FC<AssistantLauncherProps> = ({
  tenantId,
  pageContext,
  copy,
  onOpenChange,
  entryContainer,
}) => {
  const [config, setConfig] = useState<ConfigState>('loading');
  const page = pageContext?.page;

  useEffect(() => {
    let live = true;
    setConfig('loading');
    if (!tenantId || !page) {
      setConfig('off');
      return undefined;
    }
    getAssistantConfig(tenantId).then(
      (c) => {
        if (live) setConfig(c.enabled && c.pages.includes(page) ? 'on' : 'off');
      },
      () => {
        if (live) setConfig('off');
      },
    );
    return () => {
      live = false;
    };
  }, [tenantId, page]);

  if (config !== 'on' || !pageContext) return null;
  return (
    <EnabledLauncher
      pageContext={pageContext}
      copy={copy}
      onOpenChange={onOpenChange}
      entryContainer={entryContainer}
    />
  );
};

const EnabledLauncher: React.FC<{
  pageContext: PageContext;
  copy: AssistantPageCopy;
  onOpenChange?: (open: boolean) => void;
  entryContainer?: HTMLElement | null;
}> = ({ pageContext, copy, onOpenChange, entryContainer }) => {
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [bandVisible, setBandVisible] = useState(true);
  const bandRef = useRef<HTMLButtonElement | null>(null);
  const pillRef = useRef<HTMLButtonElement | null>(null);
  const openerRef = useRef<'band' | 'pill'>('band');
  const desktop = useIsDesktop();
  const navHeight = useNavHeight(open);
  const convo = useAssistantConversation(pageContext, hasOpened);
  const { stop } = convo;
  const streaming = convo.state.status === 'streaming';

  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  useEffect(() => {
    onOpenChangeRef.current?.(open);
  }, [open]);
  // Unmount: the page must not stay reflowed.
  useEffect(() => () => onOpenChangeRef.current?.(false), []);

  const bandShown = entryContainer !== null;

  // Floating pill only while the band control is out of view.
  useEffect(() => {
    const el = bandRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setBandVisible(true);
      return undefined;
    }
    const io = new IntersectionObserver(([entry]) => setBandVisible(entry.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, [entryContainer]);

  const openFrom = (who: 'band' | 'pill') => {
    openerRef.current = who;
    setHasOpened(true);
    setOpen(true);
  };

  const close = useCallback(() => {
    if (streaming) stop();
    setOpen(false);
    // Focus returns to whichever control opened it; the band if that's gone.
    const target = openerRef.current === 'pill' && pillRef.current ? pillRef.current : bandRef.current;
    setTimeout(() => target?.focus({ preventScroll: openerRef.current === 'pill' }), 0);
  }, [streaming, stop]);

  const toggleFromBand = () => {
    if (open && desktop) close();
    else openFrom('band');
  };

  const portalTarget = typeof document !== 'undefined' ? document.body : null;

  const bandButton = (
    <button
      ref={bandRef}
      type="button"
      data-testid="assistant-launcher"
      aria-expanded={open}
      aria-controls={ASSISTANT_PANEL_ID}
      onClick={toggleFromBand}
      className={`mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-full bg-white text-ink text-sm font-semibold shadow-sm hover:bg-cream focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-coral ${
        open ? 'ring-2 ring-white/70 ring-offset-2 ring-offset-coral' : ''
      }`}
    >
      <Sparkles className="w-4 h-4 text-coral" aria-hidden />
      {copy.entryLabel}
    </button>
  );

  return (
    <>
      {entryContainer === undefined
        ? bandButton
        : entryContainer
          ? createPortal(bandButton, entryContainer)
          : null}
      {portalTarget &&
        createPortal(
          <>
            {!open && bandShown && !bandVisible && (
              <button
                ref={pillRef}
                type="button"
                data-testid="assistant-pill"
                aria-controls={ASSISTANT_PANEL_ID}
                aria-expanded={false}
                onClick={() => openFrom('pill')}
                className="fixed z-40 bottom-4 right-4 md:bottom-5 md:right-5 inline-flex items-center gap-2 h-11 pl-3.5 pr-4 rounded-full bg-ink text-white text-sm font-semibold shadow-lg hover:bg-ink/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40 focus-visible:ring-offset-2"
              >
                <Sparkles className="w-4 h-4 text-gold" aria-hidden />
                <span className="sm:hidden">{ASSISTANT_COPY.ask}</span>
                <span className="hidden sm:inline">{copy.entryLabel}</span>
              </button>
            )}
            {open && (
              <AssistantPanelBoundary
                onClose={close}
                presentation={desktop ? 'docked' : 'sheet'}
                navHeight={navHeight}
              >
                <AssistantPanel
                  convo={convo}
                  copy={copy}
                  presentation={desktop ? 'docked' : 'sheet'}
                  onClose={close}
                  navHeight={navHeight}
                />
              </AssistantPanelBoundary>
            )}
          </>,
          portalTarget,
        )}
    </>
  );
};

export default AssistantLauncher;
